import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react'
import { CALCULATION_ROUNDING_STEPS, erfuelltRecht, geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { inlineInput } from '@/lib/form-styles'
import { geldEingabe } from '@/components/geldfeld'
import { gleich, leseZahl, useWarnungBeimVerlassen, zahlText } from '@/lib/speichern'
import { applyPricing, fetchPricing, previewPricing } from '../api/calculation-pricing.api'
import { fetchSchemes } from '../api/calculations.api'
import { KOSTENARTEN, KOSTENART_KURZ, KOSTENART_TEXT } from '../lib/kalkulation-labels'
import type { CalcClosing, CalcPricing, CalcPricingDraft, CalcPricingPhaseRate, CalcPricingSurcharge, CalcScheme, Calculation } from '../types/calculation.types'
import { Personalpreis } from './Personalpreis'
import { Schlussblatt } from './Schlussblatt'

interface Entwurf {
  rates: { qualificationCode: string; name: string; costRate: number | null; billingRate: number | null; isDefault: boolean; tenantCostRate: number | null; tenantBillingRate: number | null; tenantChanged: boolean; inCalculation: boolean }[]
  /** Nachtrag 9 (N9-2): Standardsatz je Phase. */
  phaseRates: CalcPricingPhaseRate[]
  surcharges: CalcPricingSurcharge[]
  laborMode: 'COST_PLUS' | 'BILLING_RATE'
  roundingStep: number
  discountPercent: number | null
  schemeName: string | null
}

const ausStand = (p: CalcPricing): Entwurf => ({
  rates: p.rates.map((r) => ({ ...r })),
  phaseRates: (p.phaseRates ?? []).map((x) => ({ ...x })),
  surcharges: p.surcharges.map(({ id, name, percent, appliesTo, basis, overrides }) => ({ id, name, percent, appliesTo: [...appliesTo], basis, overrides })),
  laborMode: p.laborMode,
  roundingStep: p.roundingStep,
  discountPercent: p.discountPercent,
  schemeName: p.schemeName,
})

/** Was sich gegenüber dem gespeicherten Stand geändert hat — nur das geht hinaus. */
function alsDraft(e: Entwurf, basis: Entwurf): CalcPricingDraft {
  const d: CalcPricingDraft = {}
  // Neu beim Mandanten zählt erst, wenn jemand ihn übernommen hat (`inCalculation`).
  const rates = e.rates.filter((r) => {
    if (!r.inCalculation) return false
    const b = basis.rates.find((x) => x.qualificationCode === r.qualificationCode)
    return !b || !b.inCalculation || b.costRate !== r.costRate || b.billingRate !== r.billingRate || b.isDefault !== r.isDefault
  })
  if (rates.length) d.rates = rates.map((r) => ({ qualificationCode: r.qualificationCode, costRate: r.costRate, billingRate: r.billingRate, isDefault: r.isDefault }))
  const phasen = e.phaseRates.filter((p) => basis.phaseRates.find((x) => x.phaseKey === p.phaseKey)?.qualificationCode !== p.qualificationCode)
  if (phasen.length) d.phaseRates = phasen.map((p) => ({ phaseKey: p.phaseKey, qualificationCode: p.qualificationCode }))
  const ohneZahl = (l: CalcPricingSurcharge[]) => l.map(({ id, name, percent, appliesTo, basis: b }) => ({ id, name, percent, appliesTo, basis: b }))
  if (!gleich(ohneZahl(e.surcharges), ohneZahl(basis.surcharges))) d.surcharges = ohneZahl(e.surcharges)
  if (e.laborMode !== basis.laborMode) d.laborMode = e.laborMode
  if (e.roundingStep !== basis.roundingStep) d.roundingStep = e.roundingStep
  if (e.discountPercent !== basis.discountPercent) d.discountPercent = e.discountPercent
  if (e.schemeName !== basis.schemeName) d.schemeName = e.schemeName
  return d
}

/** Zahl eingeben, beim Verlassen gelesen — der Entwurf bekommt nur gültige Werte. */
function ZahlFeld({
  wert,
  onWert,
  gesperrt,
  platzhalter,
  klasse = 'w-24',
  stellen = 2,
  geld,
}: {
  wert: number | null
  onWert: (n: number | null) => void
  gesperrt?: boolean
  platzhalter?: string
  klasse?: string
  stellen?: number
  /** N1-33: ein Betrag — ohne Fokus `1.234,50`, beim Bearbeiten ohne Tausenderpunkt. */
  geld?: boolean
}) {
  const alsText = (n: number | null) => (geld ? geldEingabe(n) : zahlText(n, stellen))
  const [text, setText] = useState(alsText(wert))
  const [fehler, setFehler] = useState(false)
  const [fokus, setFokus] = useState(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setText(alsText(wert)), [wert, stellen, geld])
  const zahl = leseZahl(text)
  return (
    <input
      className={`${inlineInput} ${klasse} text-right tabular-nums ${fehler ? 'border-destructive' : ''}`}
      inputMode="decimal"
      value={geld && !fokus && zahl !== null ? geldText(zahl) : text}
      placeholder={platzhalter}
      disabled={gesperrt}
      onFocus={() => setFokus(true)}
      onChange={(e) => setText(e.target.value.replace(/[^0-9,.\-+\s]/g, ''))}
      onBlur={() => {
        setFokus(false)
        const n = leseZahl(text)
        if (text.trim() !== '' && n === null) return setFehler(true)
        setFehler(false)
        onWert(n)
      }}
    />
  )
}

/**
 * AP-37 9. — **Reiter Sätze & Aufschläge**: der **einzige** Ort, an dem Sätze,
 * Schema, Lohnmodus, Rundung und Nachlass einer Kalkulation geändert werden.
 * Links die Sätze je Qualifikation (Mandantensatz daneben, nie automatisch),
 * rechts das Schema. Jede Änderung zeigt vorher ihre Wirkung (Schlussblatt
 * vorher → nachher) und rechnet erst mit **Übernehmen** alle Positionen neu —
 * ein Vorgang als Einheit mit dem Verb als Knopf (AP-36 Fall 3, F-4).
 */
export function SaetzeTab({ kalkulation, onGeaendert, onZumSchlussblatt }: { kalkulation: Calculation; onGeaendert: () => void; onZumSchlussblatt?: () => void }) {
  const { user } = useAuthStore()
  const darf = kalkulation.writable && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const [stand, setStand] = useState<CalcPricing | null>(null)
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null)
  const [vorschau, setVorschau] = useState<{ before: CalcClosing; after: CalcClosing } | null>(null)
  const [schemata, setSchemata] = useState<CalcScheme[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const laden = useCallback(async () => {
    try {
      const p = await fetchPricing(kalkulation.id)
      setStand(p)
      setEntwurf(ausStand(p))
      setVorschau(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Sätze und Aufschläge ließen sich nicht laden.'))
    }
  }, [kalkulation.id])
  useEffect(() => {
    void laden()
    fetchSchemes().then(setSchemata).catch(() => setSchemata([]))
  }, [laden])

  const basis = useMemo(() => (stand ? ausStand(stand) : null), [stand])
  const draft = useMemo(() => (entwurf && basis ? alsDraft(entwurf, basis) : {}), [entwurf, basis])
  const geaendert = Object.keys(draft).length > 0
  useWarnungBeimVerlassen(geaendert)

  // Wirkung vorrechnen, sobald sich der Entwurf ändert (gedrosselt).
  const zaehler = useRef(0)
  useEffect(() => {
    if (!geaendert) {
      setVorschau(null)
      return
    }
    const nr = ++zaehler.current
    const t = setTimeout(() => {
      previewPricing(kalkulation.id, draft)
        .then((v) => nr === zaehler.current && setVorschau(v))
        .catch((e) => nr === zaehler.current && setFehler(apiErrorText(e, 'Die Wirkung ließ sich nicht vorrechnen.')))
    }, 350)
    return () => clearTimeout(t)
  }, [draft, geaendert, kalkulation.id])

  if (!stand || !entwurf) return <p className="text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</p>

  const aendere = (f: (e: Entwurf) => Entwurf) => {
    setFehler(null)
    setEntwurf((e) => (e ? f(e) : e))
  }
  const satz = (code: string, teil: Partial<Entwurf['rates'][number]>) =>
    aendere((e) => ({ ...e, rates: e.rates.map((r) => (r.qualificationCode === code ? { ...r, ...teil } : r)) }))
  const zuschlag = (i: number, teil: Partial<CalcPricingSurcharge>) => aendere((e) => ({ ...e, surcharges: e.surcharges.map((z, j) => (j === i ? { ...z, ...teil } : z)) }))
  const schiebe = (i: number, um: number) =>
    aendere((e) => {
      const l = [...e.surcharges]
      const [z] = l.splice(i, 1)
      l.splice(i + um, 0, z)
      return { ...e, surcharges: l }
    })

  const uebernehmen = async () => {
    setLaeuft(true)
    setFehler(null)
    try {
      const p = await applyPricing(kalkulation.id, draft, stand.updatedAt)
      setStand(p)
      setEntwurf(ausStand(p))
      setVorschau(null)
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das ließ sich nicht übernehmen.'))
    } finally {
      setLaeuft(false)
    }
  }

  const schemaLaden = async (id: string) => {
    const s = schemata.find((x) => x.id === id)
    if (!s) return
    const ueberschrieben = entwurf.surcharges.reduce((n, z) => n + (z.overrides ?? 0), 0)
    if (
      !(await bestaetige(
        `Schema „${s.name}" laden? Es ersetzt die Aufschläge dieser Kalkulation${ueberschrieben ? ` — ${ueberschrieben} Abweichung(en) an Positionen fallen weg` : ''}. Gerechnet wird erst mit „Übernehmen".`,
        { ok: 'Laden' },
      ))
    )
      return
    aendere((e) => ({
      ...e,
      schemeName: s.name,
      surcharges: s.rows.map((r) => ({ name: r.name, percent: r.percent ?? 0, appliesTo: [...r.appliesTo], basis: r.basis })),
    }))
  }

  const gesperrt = !darf
  // Nachtrag 9: Wählbar als Standardsatz ist, was die Kalkulation als Satz führt — mit dem Satz des geltenden Lohnmodus.
  const waehlbar = entwurf.rates.filter((r) => r.inCalculation)
  const standard = waehlbar.find((r) => r.isDefault)
  const satzText = (r: Entwurf['rates'][number]) => {
    const satz = entwurf.laborMode === 'BILLING_RATE' ? r.billingRate : r.costRate
    return satz == null ? r.name : `${r.name} · ${geldText(satz)}`
  }
  const phasenGeaendert = Boolean(draft.phaseRates?.length)
  const summeAlt = vorschau?.before.priceAfterDiscount ?? stand.closing.priceAfterDiscount
  const summeNeu = vorschau?.after.priceAfterDiscount

  return (
    <div className="space-y-4">
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
      {!darf && <p className="text-xs text-muted-foreground">Nur lesbar — {kalkulation.writable ? 'dafür fehlt das Recht, Kalkulationen zu bearbeiten.' : 'die Fassung ist nicht in Arbeit.'}</p>}

      <div className="grid gap-4 xl:grid-cols-2">
        {/* ── Sätze ─────────────────────────────────────────────── */}
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 text-sm font-semibold">Sätze je Qualifikation</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="py-1 text-left font-normal">Qualifikation</th>
                <th className="py-1 text-right font-normal">Kostensatz €/h</th>
                <th className="py-1 text-right font-normal">Verrechnung €/h</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entwurf.rates.map((r) => {
                const b = basis?.rates.find((x) => x.qualificationCode === r.qualificationCode)
                return (
                  <tr key={r.qualificationCode} className={`border-t border-border/60 ${r.inCalculation ? '' : 'text-muted-foreground'}`}>
                    <td className="py-1 pr-2">
                      {r.name}
                      {!r.inCalculation && <span className="ml-1 text-xs">(neu in den Vorgabesätzen)</span>}
                    </td>
                    <td className="py-1 text-right">
                      <ZahlFeld geld wert={r.costRate} gesperrt={gesperrt || !r.inCalculation} onWert={(n) => satz(r.qualificationCode, { costRate: n })} platzhalter="—" />
                    </td>
                    <td className="py-1 text-right">
                      <ZahlFeld geld wert={r.billingRate} gesperrt={gesperrt || !r.inCalculation} onWert={(n) => satz(r.qualificationCode, { billingRate: n })} platzhalter="—" />
                    </td>
                    <td className="py-1 pl-2 text-xs">
                      {b?.tenantChanged && (
                        <span className="flex flex-wrap items-center gap-1 text-amber-700 dark:text-amber-400">
                          Vorgabesatz {geldText(r.tenantCostRate) || '—'} / {geldText(r.tenantBillingRate) || '—'}
                          {darf && (
                            <button
                              type="button"
                              className="text-primary hover:underline"
                              // Ein leerer Vorgabesatz überschreibt den Satz der Kalkulation nicht (4.5).
                              onClick={() => satz(r.qualificationCode, { costRate: r.tenantCostRate ?? r.costRate, billingRate: r.tenantBillingRate ?? r.billingRate, inCalculation: true })}
                            >
                              übernehmen
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* Nachtrag 9 (N9-2): Standardsätze — einer allgemein (bisher der Punkt in der Tabelle) und einer je Phase. */}
          <div className="mt-4 rounded-md border border-border/70 p-3">
            <h4 className="text-sm font-medium">Standardsätze</h4>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Ein Arbeitsschritt bekommt den Standardsatz seiner Kategorie (Phase), solange du an ihm keine andere Qualifikation wählst — ohne Kategorie oder ohne eigenen
              Standardsatz rechnet er mit dem allgemeinen.
            </p>
            <div className="mt-2 grid gap-x-3 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              <label className="text-sm">
                <span className="block text-xs text-muted-foreground">Allgemein</span>
                <select
                  className={inlineInput}
                  value={standard?.qualificationCode ?? ''}
                  disabled={gesperrt}
                  onChange={(e) => aendere((x) => ({ ...x, rates: x.rates.map((r) => ({ ...r, isDefault: r.qualificationCode === e.target.value })) }))}
                >
                  {!standard && <option value="">— keiner —</option>}
                  {waehlbar.map((r) => (
                    <option key={r.qualificationCode} value={r.qualificationCode}>
                      {satzText(r)}
                    </option>
                  ))}
                </select>
              </label>
              {entwurf.phaseRates.map((p) => {
                const b = basis?.phaseRates.find((x) => x.phaseKey === p.phaseKey)
                const fremd = p.qualificationCode && !waehlbar.some((r) => r.qualificationCode === p.qualificationCode) ? p.qualificationCode : null
                return (
                  <label key={p.phaseKey} className="text-sm">
                    <span className="block text-xs text-muted-foreground">
                      {p.name}
                      {b && b.qualificationCode !== p.qualificationCode && <span className="ml-1 text-amber-700 dark:text-amber-400">geändert</span>}
                    </span>
                    <select
                      className={inlineInput}
                      value={p.qualificationCode ?? ''}
                      disabled={gesperrt}
                      title={`Standardsatz für Arbeitsschritte der Kategorie „${p.name}"`}
                      onChange={(e) =>
                        aendere((x) => ({ ...x, phaseRates: x.phaseRates.map((y) => (y.phaseKey === p.phaseKey ? { ...y, qualificationCode: e.target.value || null } : y)) }))
                      }
                    >
                      <option value="">— allgemeiner Standardsatz —</option>
                      {/* Aus den Einstellungen vorgegeben, aber kein Satz dieser Kalkulation: sichtbar, damit klar ist, was gilt. */}
                      {fremd && <option value={fremd}>{fremd} (kein Satz in dieser Kalkulation)</option>}
                      {waehlbar.map((r) => (
                        <option key={r.qualificationCode} value={r.qualificationCode}>
                          {satzText(r)}
                        </option>
                      ))}
                    </select>
                  </label>
                )
              })}
            </div>
            {phasenGeaendert && (
              <p className="mt-2 text-xs text-muted-foreground">
                Mit „Übernehmen" stellen sich die Arbeitsschritte der geänderten Kategorien um, die noch den bisherigen Standardsatz tragen oder keine Qualifikation haben — eine eigens
                gewählte Qualifikation bleibt.
              </p>
            )}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <label className="text-sm">
              <span className="block text-xs text-muted-foreground">Lohnmodus</span>
              <select className={inlineInput} value={entwurf.laborMode} disabled={gesperrt} onChange={(e) => aendere((x) => ({ ...x, laborMode: e.target.value as Entwurf['laborMode'] }))}>
                <option value="COST_PLUS">Kostensatz + Aufschläge</option>
                <option value="BILLING_RATE">Verrechnungssatz</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-muted-foreground">EP runden auf</span>
              <select className={inlineInput} value={entwurf.roundingStep} disabled={gesperrt} onChange={(e) => aendere((x) => ({ ...x, roundingStep: Number(e.target.value) }))}>
                {CALCULATION_ROUNDING_STEPS.map((s) => (
                  <option key={s} value={s}>
                    {geldText(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-muted-foreground">Nachlass %</span>
              <ZahlFeld klasse="w-full" wert={entwurf.discountPercent} gesperrt={gesperrt} onWert={(n) => aendere((x) => ({ ...x, discountPercent: n }))} platzhalter="keiner" />
            </label>
          </div>
          {entwurf.laborMode === 'BILLING_RATE' && (
            <p className="mt-2 text-xs text-muted-foreground">Lohn zum Verrechnungssatz — die Aufschläge wirken dann nicht auf Lohn; Kosten und Deckungsbeitrag rechnen weiter mit dem Kostensatz.</p>
          )}
        </section>

        {/* ── Aufschläge ────────────────────────────────────────── */}
        <section className="rounded-lg border border-border bg-card p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">
              Aufschläge{entwurf.schemeName ? <span className="ml-1 font-normal text-muted-foreground">— aus „{entwurf.schemeName}"</span> : null}
            </h3>
            {darf && schemata.length > 0 && (
              <select className={`${inlineInput} !w-48`} value="" onChange={(e) => void schemaLaden(e.target.value)} aria-label="Schema laden">
                <option value="">Schema laden …</option>
                {schemata.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.isDefault ? ' (Vorgabe)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="py-1 text-left font-normal">Bezeichnung</th>
                <th className="py-1 text-right font-normal">%</th>
                <th className="py-1 text-left font-normal">gilt für</th>
                <th className="py-1 text-left font-normal">Basis</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entwurf.surcharges.map((z, i) => (
                <tr key={z.id ?? `neu-${i}`} className="border-t border-border/60 align-middle">
                  <td className="py-1 pr-2">
                    <input className={inlineInput} value={z.name} disabled={gesperrt} onChange={(e) => zuschlag(i, { name: e.target.value })} aria-label="Bezeichnung" />
                    {!!z.overrides && <span className="text-[11px] text-muted-foreground">{z.overrides} Position(en) abweichend</span>}
                  </td>
                  <td className="py-1 text-right">
                    <ZahlFeld klasse="w-20" wert={z.percent} gesperrt={gesperrt} onWert={(n) => zuschlag(i, { percent: n ?? 0 })} />
                  </td>
                  <td className="py-1">
                    <span className="flex gap-0.5">
                      {KOSTENARTEN.map((k) => {
                        const an = z.appliesTo.includes(k)
                        return (
                          <button
                            key={k}
                            type="button"
                            disabled={gesperrt}
                            title={KOSTENART_TEXT[k]}
                            className={`h-6 w-6 rounded text-xs ${an ? 'bg-primary text-primary-foreground' : 'border border-border text-muted-foreground'}`}
                            onClick={() => {
                              const neu = an ? z.appliesTo.filter((x) => x !== k) : [...z.appliesTo, k]
                              if (neu.length) zuschlag(i, { appliesTo: neu })
                            }}
                          >
                            {KOSTENART_KURZ[k]}
                          </button>
                        )
                      })}
                    </span>
                  </td>
                  <td className="py-1">
                    <select className={`${inlineInput} !w-24 min-w-[6rem]`} value={z.basis} disabled={gesperrt} onChange={(e) => zuschlag(i, { basis: e.target.value as 'DIRECT' | 'RUNNING' })}>
                      <option value="DIRECT">direkt</option>
                      <option value="RUNNING">laufend</option>
                    </select>
                  </td>
                  <td className="py-1 pl-1 whitespace-nowrap">
                    {darf && (
                      <>
                        <button type="button" className="text-muted-foreground disabled:opacity-30" disabled={i === 0} onClick={() => schiebe(i, -1)} aria-label="nach oben">
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button type="button" className="text-muted-foreground disabled:opacity-30" disabled={i === entwurf.surcharges.length - 1} onClick={() => schiebe(i, 1)} aria-label="nach unten">
                          <ArrowDown className="h-4 w-4" />
                        </button>
                        <button type="button" className="ml-1 text-muted-foreground hover:text-destructive" onClick={() => aendere((e) => ({ ...e, surcharges: e.surcharges.filter((_, j) => j !== i) }))} aria-label="Zeile streichen">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {darf && (
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
              onClick={() => aendere((e) => ({ ...e, surcharges: [...e.surcharges, { name: 'Neuer Aufschlag', percent: 0, appliesTo: ['MATERIAL'], basis: 'DIRECT' }] }))}
            >
              <Plus className="h-4 w-4" /> Zeile
            </button>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            direkt = auf die Einzelkosten der Kostenart · laufend = auf den Wert bis zu dieser Zeile (Wagnis und Gewinn auf die Herstellkosten). Reihenfolge zählt. Abweichungen je Position stehen im Blatt.
          </p>
        </section>
      </div>

      <Schlussblatt closing={vorschau?.after ?? stand.closing} vorher={vorschau ? vorschau.before : null} onAendern={onZumSchlussblatt} />

      {/* Detailblatt K 6.2 — Herleitung des Kostensatzes (K3) und die Formblätter K3/K7. */}
      <Personalpreis kalkulation={kalkulation} onGeaendert={onGeaendert} />

      {darf && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 shadow-md">
          <span className="text-sm">
            Angebotssumme <span className="tabular-nums">{geldText(summeAlt)}</span>
            {geaendert && summeNeu != null && (
              <>
                {' '}→ <span className="font-semibold tabular-nums">{geldText(summeNeu)}</span>
              </>
            )}
            {geaendert && <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">nicht übernommen</span>}
          </span>
          <span className="flex gap-2">
            <button type="button" className="rounded-md border border-border px-4 py-2 text-sm disabled:opacity-40" disabled={!geaendert || laeuft} onClick={() => setEntwurf(ausStand(stand))}>
              Verwerfen
            </button>
            <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={!geaendert || laeuft} onClick={() => void uebernehmen()}>
              Übernehmen
            </button>
          </span>
        </div>
      )}
    </div>
  )
}
